import { useEffect, useState } from "react";

import ResizeObserver from "resize-observer-polyfill";

const EMPTY_SIZE = { width: 0, height: 0, scrollWidth: 0, scrollHeight: 0 };

interface UseElementSizeOptions {
  widthOnly?: boolean;
}

// Takes the element, not a ref: a ref mutating its `current` never re-runs the effect, so a node
// mounted after the first commit (a provider rendering a loader first) would never be observed.
export const useElementSize = (
  element: HTMLElement | null,
  contentElement?: HTMLElement | null,
  { widthOnly = false }: UseElementSizeOptions = {}
) => {
  const [size, setSize] = useState(EMPTY_SIZE);

  useEffect(() => {
    if (!element) {
      return;
    }

    const updateSize = () => {
      requestAnimationFrame(() => {
        const nextSize = {
          width: element.clientWidth,
          height: widthOnly ? 0 : element.clientHeight,
          scrollWidth: element.scrollWidth,
          scrollHeight: widthOnly ? 0 : element.scrollHeight,
        };

        setSize((previousSize) =>
          previousSize.width === nextSize.width &&
          previousSize.height === nextSize.height &&
          previousSize.scrollWidth === nextSize.scrollWidth &&
          previousSize.scrollHeight === nextSize.scrollHeight
            ? previousSize
            : nextSize
        );
      });
    };

    updateSize();

    const resizeObserver = new ResizeObserver(updateSize);
    resizeObserver.observe(element);
    if (contentElement) {
      resizeObserver.observe(contentElement);
    }
    window.addEventListener("resize", updateSize);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", updateSize);
    };
  }, [element, contentElement, widthOnly]);

  return size;
};
